<template>
  <CCard>
    <div
      v-if="isLoading"
      :style="{ height: '80vh' }"
      class="d-flex flex-column justify-content-center align-items-center"
    >
      <CSpinner
        style="width:4rem;height:4rem;"
        color="info"
        grow
      />
    </div>
    <div v-else>
      <CCardHeader
        component="h5"
        class="d-flex align-items-center"
      >
        <router-link
          :to="{ name: 'SellerOfferEdit', params: { id: sellerOfferId } }"
          class="mr-2"
        >
          <CIcon name="cil-arrow-left" />
        </router-link>
        Demand Insights:
        <router-link
          :to="{ name: 'SellerOfferEdit', params: { id: sellerOfferId } }"
          class="ml-1"
        >
          {{ offerName }}
        </router-link>

        <div class="d-flex align-items-center ml-auto">
          <div class="derived-schedule-toggle d-flex align-items-center mr-3">
            <CSwitch
              :checked.sync="useDerivedSchedules"
              variant="3d"
              size="sm"
              color="primary"
              class="mb-0 mr-1 flex-shrink-0"
            />
            <span class="small text-muted">Schedules based on actual call data</span>
            <span
              v-c-tooltip="{content: 'For campaigns with no open hours schedule defined (All Hours campaigns), with toggle switched on we show only the longest timeframe per day when call conversions happened. This is based on last week call data.', placement: 'top'}"
              class="ml-1 derived-schedule-help"
            >?</span>
          </div>
          <button
            type="button"
            class="btn btn-link btn-sm p-0"
            @click="viewMode = viewMode === 'heatmap' ? 'text' : 'heatmap'"
          >
            {{ viewMode === 'heatmap' ? 'Switch to Text View' : 'Switch to Heatmap View' }}
          </button>
        </div>
      </CCardHeader>
      <CCardBody>
        <div v-if="!sellerOfferInsight || !sellerOfferInsight.campaigns || sellerOfferInsight.campaigns.length === 0">
          <span class="text-muted font-italic">No demand for offer "{{ offerName }}"</span>
        </div>
        <div v-else>
          <div class="insights-stats d-flex mb-4">
            <div class="stat-item">
              <div class="stat-label">
                Active Campaigns
              </div>
              <div class="stat-value stat-value--small">
                Total - {{ totalCampaigns }}
              </div>
              <div class="stat-value stat-value--small">
                All hours - {{ allDayCampaigns }}
              </div>
              <div
                v-if="peakInfo"
                class="stat-value stat-value--small"
              >
                At peak - {{ peakInfo.count }}
              </div>
              <div
                v-if="peakInfo"
                class="stat-value stat-value--small"
              >
                At peak state - {{ peakInfo.stateCount }}
              </div>
            </div>
            <div
              v-if="peakInfo"
              class="stat-item"
            >
              <div class="stat-label">
                Peak Coverage Hours
              </div>
              <div class="stat-value stat-value--small">
                {{ peakInfo.hours }}
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-label">
                Sellers
              </div>
              <div class="stat-value stat-value--small">
                {{ totalPublishers }}
              </div>
              <div
                v-if="uncoveredTrafficSources.length"
                class="stat-uncovered"
              >
                Traffic sources without coverage: {{ visibleUncoveredSellers.join(', ') }}
                <a
                  v-if="uncoveredTrafficSources.length > 8 && !showAllSellersUncovered"
                  href
                  class="stat-show-more"
                  @click.prevent="showAllSellersUncovered = true"
                > +{{ uncoveredTrafficSources.length - 8 }} more</a>
              </div>
              <div
                v-else
                class="stat-covered"
              >
                All sellers traffic sources are covered by demand
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-label">
                Buyers
              </div>
              <div class="stat-value stat-value--small">
                {{ totalUniqueBuyers }}
              </div>
              <div
                v-if="uncoveredBySupply.length"
                class="stat-uncovered"
              >
                Traffic sources not covered by supply: {{ visibleUncoveredBuyers.join(', ') }}
                <a
                  v-if="uncoveredBySupply.length > 8 && !showAllBuyersUncovered"
                  href
                  class="stat-show-more"
                  @click.prevent="showAllBuyersUncovered = true"
                > +{{ uncoveredBySupply.length - 8 }} more</a>
              </div>
              <div
                v-else
                class="stat-covered"
              >
                All buyers traffic sources are covered by supply
              </div>
            </div>
            <div
              v-if="budgetStats"
              class="stat-item"
            >
              <div class="stat-label">
                Daily Budgets
              </div>
              <div class="stat-value stat-value--small">
                Highest -
                <router-link
                  v-if="budgetStats.daily.highestCampaignId"
                  :to="{ name: 'BuyerCampaignDetails', params: { id: budgetStats.daily.highestCampaignId } }"
                  target="_blank"
                  class="stat-link"
                >
                  ${{ budgetStats.daily.highest }}
                </router-link>
                <span v-else>{{ budgetStats.daily.highest != null ? '$' + budgetStats.daily.highest : 'N/A' }}</span>
              </div>
              <div class="stat-value stat-value--small">
                Lowest -
                <router-link
                  v-if="budgetStats.daily.lowestCampaignId"
                  :to="{ name: 'BuyerCampaignDetails', params: { id: budgetStats.daily.lowestCampaignId } }"
                  target="_blank"
                  class="stat-link"
                >
                  ${{ budgetStats.daily.lowest }}
                </router-link>
                <span v-else>{{ budgetStats.daily.lowest != null ? '$' + budgetStats.daily.lowest : 'N/A' }}</span>
              </div>
              <div class="stat-value stat-value--small">
                Average - {{ budgetStats.daily.average != null ? '$' + budgetStats.daily.average : 'N/A' }}
              </div>
              <div class="stat-value stat-value--small">
                Total - {{ budgetStats.daily.total != null ? '$' + budgetStats.daily.total : 'N/A' }}
              </div>
            </div>
            <div
              v-if="spendStats"
              class="stat-item"
            >
              <div class="stat-label">
                Daily Spendings
                <span
                  v-c-tooltip="{content: 'All spent budgets calculations are based on conversion amounts in last 7 days excluding today', placement: 'top'}"
                  class="ml-1 derived-schedule-help"
                >?</span>
              </div>
              <div class="stat-value stat-value--small">
                Highest -
                <router-link
                  :to="{ name: 'BuyerCampaignDetails', params: { id: spendStats.highestDollar.campaignId } }"
                  target="_blank"
                  class="stat-link"
                >
                  ${{ spendStats.highestDollar.value }}
                </router-link>
                /
                <router-link
                  :to="{ name: 'BuyerCampaignDetails', params: { id: spendStats.highestPercent.campaignId } }"
                  target="_blank"
                  class="stat-link"
                >
                  {{ spendStats.highestPercent.value }}%
                </router-link>
              </div>
              <div class="stat-value stat-value--small">
                Lowest -
                <router-link
                  :to="{ name: 'BuyerCampaignDetails', params: { id: spendStats.lowestDollar.campaignId } }"
                  target="_blank"
                  class="stat-link"
                >
                  ${{ spendStats.lowestDollar.value }}
                </router-link>
                /
                <router-link
                  :to="{ name: 'BuyerCampaignDetails', params: { id: spendStats.lowestPercent.campaignId } }"
                  target="_blank"
                  class="stat-link"
                >
                  {{ spendStats.lowestPercent.value }}%
                </router-link>
              </div>
              <div class="stat-value stat-value--small">
                Average - ${{ spendStats.averageDollar }} / {{ spendStats.averagePercent }}%
              </div>
              <div class="stat-value stat-value--small">
                Avg Total - ${{ spendStats.totalDollar }}
              </div>
            </div>
          </div>
          <DemandInsightsHeatmap
            v-if="viewMode === 'heatmap'"
            :insight="effectiveInsight"
            :peak-campaign-count="peakInfo ? peakInfo.stateCount : 0"
            :combined-zipcodes="combinedZipcodes"
            :zipcodes-loading="zipcodesLoading"
            @filter-change="onFilterChange"
            @download-zipcodes="downloadZipcodes"
          />
          <DemandInsightsText
            v-else
            :insight="effectiveInsight"
          />
        </div>
      </CCardBody>
    </div>
  </CCard>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { SLOTS_PER_DAY, TOTAL_SLOTS, PEAK_THRESHOLD } from '@/store/modules/insights.store'
import DemandInsightsHeatmap from '@/components/DemandInsightsHeatmap.vue'
import DemandInsightsText from '@/components/DemandInsightsText.vue'

export default {
  name: 'SellerOfferInsightsPage',

  components: {
    DemandInsightsHeatmap,
    DemandInsightsText
  },

  data() {
    return {
      sellerOfferId: null,
      isLoading: false,
      viewMode: 'heatmap',
      useDerivedSchedules: true,
      showAllSellersUncovered: false,
      showAllBuyersUncovered: false,
      filterStates: null,
      filterTrafficSources: null,
      combinedZipcodes: [],
      zipcodesLoading: false,
      zipcodeCampaignCount: 0
    }
  },

  computed: {
    ...mapState('sellerOffer', ['selectedRow']),
    ...mapState('insights', ['sellerOfferInsight']),

    offerName() {
      return this.selectedRow?.name || ''
    },

    campaigns() {
      const raw = this.sellerOfferInsight?.campaigns || []
      if (!this.useDerivedSchedules) {
        return raw
      }

      return raw
        .filter(c => !c.isAllHours || (c.dailySchedule && c.dailySchedule.length > 0))
        .map(c => {
          if (c.isAllHours && c.dailySchedule && c.dailySchedule.length > 0) {
            return { ...c, isAllHours: false }
          }

          return c
        })
    },

    effectiveInsight() {
      if (!this.sellerOfferInsight) {
        return null
      }

      return { ...this.sellerOfferInsight, campaigns: this.campaigns }
    },

    filteredCampaigns() {
      return this.campaigns.filter(c => {
        if (!this.matchesStateFilter(c)) {
          return false
        }

        if (!this.matchesTrafficSourceFilter(c)) {
          return false
        }

        return true
      })
    },

    totalCampaigns() {
      return this.filteredCampaigns.length
    },

    allDayCampaigns() {
      return this.filteredCampaigns.filter(s => s.isAllHours).length
    },

    sellerRegistrations() {
      return this.sellerOfferInsight?.sellerRegistrations || []
    },

    filteredSellerRegistrations() {
      if (!this.filterTrafficSources) {
        return this.sellerRegistrations
      }

      if (!this.filterTrafficSources.length) {
        return []
      }

      return this.sellerRegistrations.filter(r =>
        r.trafficSourceName && this.filterTrafficSources.includes(r.trafficSourceName)
      )
    },

    totalPublishers() {
      return this.filteredSellerRegistrations.length
    },

    coveredTrafficSources() {
      const set = new Set()
      this.filteredCampaigns.forEach(c => {
        if (c.trafficSourceNames) {
          c.trafficSourceNames.forEach(name => set.add(name))
        }
      })
      return set
    },

    allRegistrationTrafficSources() {
      const set = new Set()
      this.filteredSellerRegistrations.forEach(r => {
        if (r.trafficSourceName) {
          set.add(r.trafficSourceName)
        }
      })
      return Array.from(set).sort()
    },

    uncoveredTrafficSources() {
      return this.allRegistrationTrafficSources.filter(
        name => !this.coveredTrafficSources.has(name)
          && (!this.filterTrafficSources || this.filterTrafficSources.includes(name))
      )
    },

    visibleUncoveredSellers() {
      if (this.showAllSellersUncovered) {
        return this.uncoveredTrafficSources
      }

      return this.uncoveredTrafficSources.slice(0, 8)
    },

    totalUniqueBuyers() {
      const buyerIds = new Set()
      this.filteredCampaigns.forEach(c => {
        if (c.buyerId) {
          buyerIds.add(c.buyerId)
        }
      })
      return buyerIds.size
    },

    approvedTrafficSources() {
      const set = new Set()
      this.filteredSellerRegistrations.forEach(r => {
        if (r.approvedTrafficSourceNames) {
          r.approvedTrafficSourceNames.forEach(name => set.add(name))
        }
      })
      return set
    },

    uncoveredBySupply() {
      const campaignSources = new Set()
      this.filteredCampaigns.forEach(c => {
        if (c.trafficSourceNames) {
          c.trafficSourceNames.forEach(name => {
            if (!this.filterTrafficSources || this.filterTrafficSources.includes(name)) {
              campaignSources.add(name)
            }
          })
        }
      })
      return Array.from(campaignSources).sort().filter(
        name => !this.approvedTrafficSources.has(name)
      )
    },

    visibleUncoveredBuyers() {
      if (this.showAllBuyersUncovered) {
        return this.uncoveredBySupply
      }

      return this.uncoveredBySupply.slice(0, 8)
    },

    budgetStats() {
      const entries = []

      this.filteredCampaigns.forEach(c => {
        if (!c.budget || !c.budget.dailyBudgetInUSD) {
          return
        }

        entries.push({
          daily: c.budget.dailyBudgetInUSD,
          campaignId: c.campaignId
        })
      })

      if (!entries.length) {
        return null
      }

      entries.sort((a, b) => b.daily - a.daily)
      const dailyValues = entries.map(e => e.daily)

      return {
        daily: {
          highest: entries[0].daily,
          highestCampaignId: entries[0].campaignId,
          lowest: entries[entries.length - 1].daily,
          lowestCampaignId: entries[entries.length - 1].campaignId,
          average: Math.round(dailyValues.reduce((sum, v) => sum + v, 0) / dailyValues.length),
          total: dailyValues.reduce((sum, v) => sum + v, 0)
        }
      }
    },

    spendStats() {
      const dailyEntries = []
      const weeklyAvgs = []
      let totalSpend = 0
      let totalBudgetWeekly = 0

      this.filteredCampaigns.forEach(c => {
        if (!c.weeklySpendBudget || c.weeklySpendBudget.length !== 7 || !c.budget || !c.budget.dailyBudgetInUSD) {
          return
        }

        const dailyBudget = c.budget.dailyBudgetInUSD
        const weeklySpend = c.weeklySpendBudget.reduce((sum, v) => sum + v, 0)
        totalSpend += weeklySpend
        totalBudgetWeekly += dailyBudget * 7

        const nonZeroDays = c.weeklySpendBudget.filter(s => s !== 0)
        c.weeklySpendBudget.forEach(spent => {
          let pct = (spent / dailyBudget) * 100
          if (pct < 0) {
            pct = pct + 100
          }

          pct = Math.round(pct)

          dailyEntries.push({ dollar: Math.round(spent), percent: pct, campaignId: c.campaignId })
        })

        const avgDollar = nonZeroDays.length
          ? Math.round(nonZeroDays.reduce((sum, v) => sum + v, 0) / nonZeroDays.length)
          : 0
        weeklyAvgs.push({ avgDollar, dailyBudget })
      })

      if (!dailyEntries.length) {
        return null
      }

      const byDollar = [...dailyEntries].sort((a, b) => b.dollar - a.dollar)
      const byPercent = [...dailyEntries].sort((a, b) => b.percent - a.percent)
      const nonZeroDollar = byDollar.filter(e => e.dollar > 0)
      const nonZeroPercent = byPercent.filter(e => e.percent > 0)
      const lowestDollarEntry = nonZeroDollar.length ? nonZeroDollar[nonZeroDollar.length - 1] : byDollar[byDollar.length - 1]
      const lowestPercentEntry = nonZeroPercent.length ? nonZeroPercent[nonZeroPercent.length - 1] : byPercent[byPercent.length - 1]

      return {
        highestDollar: { value: byDollar[0].dollar, campaignId: byDollar[0].campaignId },
        highestPercent: { value: byPercent[0].percent, campaignId: byPercent[0].campaignId },
        lowestDollar: { value: lowestDollarEntry.dollar, campaignId: lowestDollarEntry.campaignId },
        lowestPercent: { value: lowestPercentEntry.percent, campaignId: lowestPercentEntry.campaignId },
        averageDollar: Math.round(weeklyAvgs.reduce((sum, e) => sum + e.avgDollar, 0) / weeklyAvgs.length),
        averagePercent: Math.round((weeklyAvgs.reduce((sum, e) => sum + e.avgDollar, 0) / weeklyAvgs.reduce((sum, e) => sum + e.dailyBudget, 0)) * 100),
        totalDollar: Math.round(totalSpend / 7),
        totalPercent: totalBudgetWeekly > 0 ? Math.round((totalSpend / totalBudgetWeekly) * 100) : 0
      }
    },

    peakInfo() {
      const campaigns = this.filteredCampaigns
      if (!campaigns.length) {
        return null
      }

      const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

      let maxCount = 0
      let globalMaxCount = 0
      const slotMaxCounts = new Array(TOTAL_SLOTS).fill(0)

      for (let i = 0; i < TOTAL_SLOTS; i++) {
        const dayIdx = Math.floor(i / SLOTS_PER_DAY)
        const halfHour = i % SLOTS_PER_DAY
        const timeMinutes = Math.floor(halfHour / 2) * 60 + (halfHour % 2) * 30

        let nationwideCount = 0
        let slotTotal = 0
        const stateCounts = {}

        campaigns.forEach(s => {
          if (!s.isAllHours) {
            const day = s.dailySchedule?.[dayIdx]
            if (!day || day.isClosed || !day.openTime || !day.closeTime) {
              return
            }

            const open = day.openTime.hour * 60 + day.openTime.minute
            const close = day.closeTime.hour * 60 + day.closeTime.minute
            if (timeMinutes < open || timeMinutes >= close) {
              return
            }
          }

          slotTotal++

          if (!s.location || s.location.isNationwide) {
            nationwideCount++
          } else if (s.location.states) {
            s.location.states.forEach(code => {
              stateCounts[code] = (stateCounts[code] || 0) + 1
            })
          }
        })

        if (slotTotal > globalMaxCount) {
          globalMaxCount = slotTotal
        }

        let slotMax = nationwideCount
        for (const code in stateCounts) {
          const total = stateCounts[code] + nationwideCount
          if (total > slotMax) {
            slotMax = total
          }
        }

        slotMaxCounts[i] = slotMax
        if (slotMax > maxCount) {
          maxCount = slotMax
        }
      }

      if (maxCount === 0) {
        return null
      }

      const ranges = []
      const threshold = Math.ceil(maxCount * PEAK_THRESHOLD)
      let start = null
      for (let i = 0; i <= TOTAL_SLOTS; i++) {
        if (i < TOTAL_SLOTS && slotMaxCounts[i] >= threshold) {
          if (start === null) {
            start = i
          }
        } else if (start !== null) {
          ranges.push({ start, end: i })
          start = null
        }
      }

      if (ranges.length === 1 && ranges[0].start === 0 && ranges[0].end === TOTAL_SLOTS) {
        return { count: globalMaxCount, stateCount: maxCount, hours: 'All hours' }
      }

      const formatTime = (slot) => {
        const h = Math.floor((slot % SLOTS_PER_DAY) / 2)
        const m = ((slot % SLOTS_PER_DAY) % 2) * 30
        return h.toString().padStart(2, '0') + ':' + m.toString().padStart(2, '0')
      }

      const formatted = ranges.map(({ start: s, end: e }) => {
        const sDay = Math.floor(s / SLOTS_PER_DAY)
        const eDay = Math.floor((e - 1) / SLOTS_PER_DAY)
        if (sDay === eDay) {
          return `${DAY_NAMES[sDay]} ${formatTime(s)}-${formatTime(e)}`
        }

        return `${DAY_NAMES[sDay]} ${formatTime(s)} - ${DAY_NAMES[eDay]} ${formatTime(e)}`
      })

      return { count: globalMaxCount, stateCount: maxCount, hours: formatted.join(', ') }
    }
  },

  watch: {
    filteredCampaigns: {
      immediate: true,
      handler(campaigns) {
        const arrays = campaigns
          .map(c => c.location?.zipCodes || [])
          .filter(z => z.length > 0)

        this.zipcodeCampaignCount = arrays.length

        if (!arrays.length) {
          this.combinedZipcodes = []
          return
        }

        if (this.zipcodeWorker) {
          this.zipcodesLoading = true
          this.zipcodeWorker.postMessage(arrays)
        }
      }
    }
  },

  async created() {
    const workerCode = `
      self.onmessage = function(e) {
        var set = new Set()
        var arrays = e.data
        for (var i = 0; i < arrays.length; i++) {
          for (var j = 0; j < arrays[i].length; j++) {
            set.add(arrays[i][j])
          }
        }
        var result = Array.from(set).sort(function(a, b) { return a.localeCompare(b, undefined, { numeric: true }) })
        self.postMessage(result)
      }
    `
    const blob = new Blob([workerCode], { type: 'application/javascript' })
    const url = URL.createObjectURL(blob)
    this.zipcodeWorker = new Worker(url)
    URL.revokeObjectURL(url)

    this.zipcodeWorker.onmessage = (e) => {
      this.combinedZipcodes = e.data
      this.zipcodesLoading = false
    }

    this.sellerOfferId = this.$route.params.id
    this.isLoading = true

    try {
      if (!this.selectedRow || this.selectedRow.id !== this.sellerOfferId) {
        await this.findOne(this.sellerOfferId)
      }

      if (this.offerName) {
        document.title = 'Demand Insights: ' + this.offerName
      }

      await this.fetchSellerOfferInsights(this.sellerOfferId)
    } catch {
      // Error handled by API layer
    } finally {
      this.isLoading = false
    }
  },

  beforeDestroy() {
    if (this.zipcodeWorker) {
      this.zipcodeWorker.terminate()
      this.zipcodeWorker = null
    }
  },

  methods: {
    ...mapActions('sellerOffer', ['findOne']),
    ...mapActions('insights', ['fetchSellerOfferInsights']),

    onFilterChange({ states, trafficSources }) {
      this.filterStates = states
      this.filterTrafficSources = trafficSources
    },

    matchesStateFilter(campaign) {
      if (!this.filterStates) {
        return true
      }

      if (!this.filterStates.length) {
        return false
      }

      if (!campaign.location) {
        return false
      }

      if (campaign.location.isNationwide) {
        return true
      }

      if (!campaign.location.states || !campaign.location.states.length) {
        return false
      }

      return campaign.location.states.some(s => this.filterStates.includes(s))
    },

    downloadZipcodes() {
      const content = this.combinedZipcodes.join('\n')
      const blob = new Blob([content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `zipcodes ${this.offerName} (${this.zipcodeCampaignCount} campaigns combined).txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    matchesTrafficSourceFilter(campaign) {
      if (!this.filterTrafficSources) {
        return true
      }

      if (!this.filterTrafficSources.length) {
        return false
      }

      if (!campaign.trafficSourceNames || !campaign.trafficSourceNames.length) {
        return true
      }

      return campaign.trafficSourceNames.some(name =>
        this.filterTrafficSources.includes(name)
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.insights-stats {
  gap: 1px;
  background: #d8dbe0;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  overflow: hidden;
}

.stat-item {
  flex: 1;
  padding: 12px 16px;
  background: #fff;
  text-align: center;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: #321fdb;

  &--small {
    font-size: 0.8rem;
  }
}

.stat-label {
  font-size: 0.75rem;
  color: #768192;
  text-transform: uppercase;
  margin-top: 2px;
}

.stat-uncovered {
  margin-top: 6px;
  border-top: 1px solid #d8dbe0;
  padding-top: 6px;
  font-size: 0.7rem;
  color: #e55353;
}

.stat-covered {
  margin-top: 6px;
  border-top: 1px solid #d8dbe0;
  padding-top: 6px;
  font-size: 0.7rem;
  color: #2eb85c;
}

.stat-link {
  color: #321fdb;
  text-decoration: underline;
  text-decoration-style: dotted;

  &:hover {
    color: #1f14a0;
    text-decoration-style: solid;
  }
}

.stat-show-more {
  color: #321fdb;
  cursor: pointer;
  white-space: nowrap;
}

.derived-schedule-toggle {
  ::v-deep .c-switch {
    transform: scale(0.7);
    transform-origin: center;
  }
}

.derived-schedule-help {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #768192;
  color: #fff;
  font-size: 0.6rem;
  font-weight: 700;
  cursor: help;
  flex-shrink: 0;
}

</style>
